import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Inject, Injectable, LOCALE_ID } from '@angular/core';

import { ONBOARDING_API_CONFIG, OnboardingApiConfigInterface } from '@planone/bau365/data-access-onboarding';

import {
  ExhibitorMockGenerator,
  MOCK_API,
  MockEnvironmentInterface,
  ProductMockGenerator,
} from '@planone/bau365/util-api-response-mock-generators';
import { Exhibitor, Product } from '@planone/bau365/util-fair-data';
import { DataAccessTogglesFacade } from '@planone/shared/data-access-feature-flags';
import { map, of, throwError } from 'rxjs';

import {
  CombinedSearchResult,
  ExhibitorListRequest,
  ExhibitorProductsResponse,
  ExhibitorResponse,
  ExhibitorSearchFilters,
  Facet,
} from '../../interfaces/exhibitor.interfaces';

interface SearchServiceResult {
  facets: { string_facets: Facet[] };
  counts: { exhibitors?: number; brands?: number };
  results: CombinedSearchResult[];
}

enum SearchType {
  all = 'ALL',
  exhibitor = 'EXHIBITOR',
  product = 'PRODUCT',
  event = 'EVENT',
  post = 'POST',
}

interface SearchSettings {
  search_string: string;
  search_type: SearchType[];
  sort: {
    by: 'RECOMMENDED' | 'ALPHABETICAL' | 'DATE';
    order: 'ASC' | 'DESC';
  };
}

@Injectable()
export class DataAccessExhibitorService {
  defaultSearchSettings: SearchSettings = {
    search_string: '',
    search_type: [SearchType.all],
    sort: {
      by: 'RECOMMENDED',
      order: 'ASC',
    },
  };

  constructor(
    private httpClient: HttpClient,
    @Inject(LOCALE_ID) private locale: string,
    @Inject(ONBOARDING_API_CONFIG)
    private onboarding_api_config: OnboardingApiConfigInterface,
    @Inject(MOCK_API) private mockConfig: MockEnvironmentInterface,
    private togglesFacade: DataAccessTogglesFacade
  ) {}

  private static emptyArrayToUndefined<T>(inputArray: T[] | null | undefined): T[] | undefined {
    if (!inputArray) return undefined;

    const filteredArray = inputArray.filter((value: T) => !!value);

    if (filteredArray.length <= 0) return undefined;

    return filteredArray;
  }

  private static mapWebsiteToValidPath(website: string) {
    const regex = /^https?:\/\//gm;
    const match = website.match(regex);

    if (match) {
      return website;
    } else {
      return 'https://' + website;
    }
  }

  private static transformExhibitor(exhibitor: Exhibitor, showContacts: boolean): Exhibitor {
    return {
      ...exhibitor,
      name: exhibitor.display_name ? exhibitor.display_name : exhibitor.name,
      videos: this.emptyArrayToUndefined(exhibitor.videos),
      gallery: this.emptyArrayToUndefined(exhibitor.gallery),
      brands: this.emptyArrayToUndefined(exhibitor.brands),
      downloads: this.emptyArrayToUndefined(exhibitor.downloads),
      homepage: exhibitor.homepage ? this.mapWebsiteToValidPath(exhibitor.homepage) : undefined,
      contact_persons: showContacts
        ? this.emptyArrayToUndefined(
            exhibitor.contact_persons?.map((contactPerson) => {
              return {
                ...contactPerson,
                website: contactPerson.website ? this.mapWebsiteToValidPath(contactPerson.website) : undefined,
              };
            })
          )
        : undefined,
      links: this.emptyArrayToUndefined(exhibitor.links),
    };
  }

  private static transformProduct(product: Product, showContacts: boolean): Product {
    return {
      ...product,
      key_facts: this.emptyArrayToUndefined(product.key_facts),
      countries: this.emptyArrayToUndefined(product.countries),
      downloads: this.emptyArrayToUndefined(product.downloads),
      videos: this.emptyArrayToUndefined(product.videos),
      links: this.emptyArrayToUndefined(product.links),
      contact_persons: showContacts
        ? this.emptyArrayToUndefined(
            product.contact_persons?.map((contactPerson) => {
              return {
                ...contactPerson,
                website: contactPerson.website ? this.mapWebsiteToValidPath(contactPerson.website) : undefined,
              };
            })
          )
        : undefined,
    };
  }

  getExhibitors(fairId: string, cursor: string | undefined = undefined, limit = 18) {
    const requestParams: ExhibitorListRequest = {
      limit: limit,
    };

    if (typeof cursor !== 'undefined') {
      requestParams['cursor'] = cursor;
    }

    if (this.mockConfig.globally) {
      return of(ExhibitorMockGenerator.getPaginatedList(fairId));
    }

    return this.httpClient
      .get<ExhibitorResponse>(`${this.onboarding_api_config.fairDataGatewayUrl}/fairs/${fairId}/exhibitors`, {
        params: { ...requestParams },
      })
      .pipe(
        map((response) => ({
          ...response,
          items: response.items.map((exhibitor: Exhibitor) =>
            DataAccessExhibitorService.transformExhibitor(exhibitor, true)
          ),
        }))
      );
  }

  getCoExhibitors(fairId: string, exhibitorId: string) {
    if (this.mockConfig.globally) {
      return of(ExhibitorMockGenerator.getList(fairId));
    }

    return this.httpClient
      .get<Exhibitor[]>(
        `${this.onboarding_api_config.fairDataGatewayUrl}/fairs/${fairId}/exhibitors/${exhibitorId}/coexhibitors`
      )
      .pipe(
        map((response) =>
          response.map((exhibitor: Exhibitor) => DataAccessExhibitorService.transformExhibitor(exhibitor, true))
        )
      );
  }

  getExhibitor(fairId: string, exhibitorId: string | undefined) {
    if (!exhibitorId) {
      throw throwError(() => ({ status: 'No exhibitor id provided' }));
    }

    const requestParams: ExhibitorListRequest = {};

    if (this.mockConfig.globally) {
      return of(ExhibitorMockGenerator.getObject(exhibitorId, fairId));
    }

    return this.httpClient
      .get<Exhibitor>(`${this.onboarding_api_config.fairDataGatewayUrl}/fairs/${fairId}/exhibitors/${exhibitorId}`, {
        params: { ...requestParams },
      })
      .pipe(map((exhibitor) => DataAccessExhibitorService.transformExhibitor(exhibitor, true)));
  }

  getExhibitorProducts(fairId: string, exhibitorId: string, cursor: string | undefined = undefined, limit = 18) {
    const requestParams: ExhibitorListRequest = {
      limit: limit,
    };

    if (typeof cursor !== 'undefined') {
      requestParams['cursor'] = cursor;
    }

    if (this.mockConfig.globally) {
      return of(ProductMockGenerator.getPaginatedList(exhibitorId));
    }

    return this.httpClient
      .get<ExhibitorProductsResponse>(
        `${this.onboarding_api_config.fairDataGatewayUrl}/fairs/${fairId}/exhibitors/${exhibitorId}/products`,
        {
          params: <any>requestParams,
        }
      )
      .pipe(
        map((response) => ({
          ...response,
          items: response.items.map((product: Product) => DataAccessExhibitorService.transformProduct(product, true)),
        }))
      );
  }

  getExhibitorDirectoryResults(filters: ExhibitorSearchFilters, fairId: string, from = 0, size = 18) {
    const defaultSearchParameters = new HttpParams().appendAll({
      from: from,
      size: size,
      with_personalization: false,
      view: 'LIST_OF_EXHIBITORS',
    });

    return this.httpClient.post<SearchServiceResult>(
      `${this.onboarding_api_config.searchUrl}/fair/${fairId}/search`,
      {
        ...this.defaultSearchSettings,
        search_type: filters.searchType,
        search_string: '',
        string_filter: filters.stringFilter,
        sort: {
          by: filters.searchSort?.split('_')[0],
          order: filters.searchSort?.split('_')[1],
        },
      },
      {
        params: defaultSearchParameters,
      }
    );
  }

  getDisplayNameSearchResults(filters: ExhibitorSearchFilters, fairId: string, from = 0, size = 20) {
    const defaultSearchParameters = new HttpParams().appendAll({ from: from, size: size, with_personalization: false });

    return this.httpClient.post<SearchServiceResult>(
      `${this.onboarding_api_config.searchUrl}/fair/${fairId}/search/display-name`,
      {
        ...this.defaultSearchSettings,
        search_type: filters.searchType,
        search_string: filters.searchTerm,
        string_filter: filters.stringFilter,
        sort: {
          by: filters.searchSort?.split('_')[0],
          order: filters.searchSort?.split('_')[1],
        },
      },
      {
        params: defaultSearchParameters,
      }
    );
  }

  getLandingPageObjects(ids: string[] | undefined) {
    if (this.mockConfig.globally) {
      return of(ExhibitorMockGenerator.getList(undefined));
    }

    return this.httpClient
      .post<Exhibitor[]>(`${this.onboarding_api_config.fairDataGatewayUrl}/landing/exhibitors`, ids, {
        headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
      })
      .pipe(
        map((exhibitors: Exhibitor[]) =>
          exhibitors
            .map((exhibitor: Exhibitor) => DataAccessExhibitorService.transformExhibitor(exhibitor, false))
            .sort((a, b) => {
              if (!ids) {
                return 0;
              }
              return ids?.indexOf(a.id) - ids?.indexOf(b.id);
            })
        )
      );
  }
}
